import React, { useState } from "react";
import { motion } from "framer-motion";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Mail, Phone, MapPin, Clock, Send } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/LanguageContext";
import axios from "axios";
import { API } from "@/App";

const ContactPage = () => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/contact`, formData);
      toast.success(res.data.message || "Melding sendt! Vi kontakter deg snart.");
      setFormData({ name: "", email: "", phone: "", subject: "", message: "" });
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke sende melding. Prov igjen.");
    }
    setSubmitting(false);
  };

  return (
    <div className="min-h-screen" style={{ background: '#f4e7d8' }}>
      <Navbar />

      {/* Header */}
      <div 
        className="py-16 mandala-pattern"
        style={{ 
          background: 'linear-gradient(135deg, #721111 0%, #2d1810 100%)'
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="indian-divider mb-4"
          >
            <span className="text-sm uppercase tracking-widest" style={{ color: '#d4af37' }}>Kontakt</span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-4xl sm:text-5xl font-serif-display font-semibold italic mb-4"
            style={{ color: '#d4af37' }}
            data-testid="contact-page-title"
          >
            {t("contact_title")}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            style={{ color: '#f5e6d3' }}
          >
            {t("contact_subtitle")}
          </motion.p>
        </div>
      </div>

      {/* Decorative Divider */}
      <div className="h-2" style={{ background: 'linear-gradient(90deg, #721111, #d4af37, #721111)' }}></div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-8"
            style={{ border: '2px solid #d4af37' }}
          >
            <h2 className="text-2xl font-serif-display font-semibold mb-6" style={{ color: '#2d1810' }}>
              Send Oss en Melding
            </h2>
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <Label htmlFor="name" style={{ color: '#2d1810' }}>Navn *</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  className="indian-input mt-1"
                  data-testid="contact-name-input"
                />
              </div>
              <div>
                <Label htmlFor="email" style={{ color: '#2d1810' }}>E-post *</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                  className="indian-input mt-1"
                  data-testid="contact-email-input"
                />
              </div>
              <div>
                <Label htmlFor="phone" style={{ color: '#2d1810' }}>Telefon</Label>
                <Input
                  id="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                  className="indian-input mt-1"
                  data-testid="contact-phone-input"
                />
              </div>
              <div>
                <Label htmlFor="subject" style={{ color: '#2d1810' }}>Emne *</Label>
                <Input
                  id="subject"
                  value={formData.subject}
                  onChange={(e) => setFormData({ ...formData, subject: e.target.value })}
                  required
                  placeholder="Catering, reservasjon, tilbakemelding, etc."
                  className="indian-input mt-1"
                  data-testid="contact-subject-input"
                />
              </div>
              <div>
                <Label htmlFor="message" style={{ color: '#2d1810' }}>Melding *</Label>
                <Textarea
                  id="message"
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                  rows={5}
                  className="indian-input mt-1"
                  data-testid="contact-message-textarea"
                />
              </div>
              <Button
                type="submit"
                disabled={submitting}
                className="w-full rounded-full font-bold py-6 btn-maroon transition-transform hover:scale-105 active:scale-95 shadow-lg"
                data-testid="contact-submit-button"
              >
                <Send className="h-5 w-5 mr-2" />
                {submitting ? "Sender..." : "Send Melding"}
              </Button>
            </form>
          </motion.div>

          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-6"
          >
            <div 
              className="bg-white rounded-xl shadow-lg p-8"
              style={{ border: '2px solid #b27f47' }}
            >
              <h2 className="text-2xl font-serif-display font-semibold mb-6" style={{ color: '#2d1810' }}>
                Kontaktinformasjon
              </h2>
              <div className="space-y-6">
                <div className="flex items-start gap-4">
                  <div 
                    className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: '#721111' }}
                  >
                    <Phone className="h-6 w-6" style={{ color: '#d4af37' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2d1810' }}>Telefon</h3>
                    <a href="tel:+4748915190" style={{ color: '#4a3728' }}>+47 48 91 51 90</a>
                    <p className="text-sm" style={{ color: '#8b4513' }}>Man-Søn: 15:00 - 22:00</p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div 
                    className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: '#721111' }}
                  >
                    <Mail className="h-6 w-6" style={{ color: '#d4af37' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2d1810' }}>E-post</h3>
                    <a href="mailto:post@masalaindisk.no" style={{ color: '#4a3728' }}>post@masalaindisk.no</a>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div 
                    className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: '#721111' }}
                  >
                    <MapPin className="h-6 w-6" style={{ color: '#d4af37' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2d1810' }}>Adresse</h3>
                    <p style={{ color: '#4a3728' }}>
                      Rikard Kaarbøs plass 1<br />
                      9405 Harstad<br />
                      Norge
                    </p>
                  </div>
                </div>

                <div className="flex items-start gap-4">
                  <div 
                    className="flex-shrink-0 w-12 h-12 rounded-full flex items-center justify-center"
                    style={{ background: '#721111' }}
                  >
                    <Clock className="h-6 w-6" style={{ color: '#d4af37' }} />
                  </div>
                  <div>
                    <h3 className="font-semibold mb-1" style={{ color: '#2d1810' }}>Åpningstider</h3>
                    <div className="text-sm space-y-1" style={{ color: '#4a3728' }}>
                      <p>Mandag - Torsdag: 15:00 - 22:00</p>
                      <p>Fredag - Lørdag: 15:00 - 23:00</p>
                      <p>Søndag: 15:00 - 22:00</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Map */}
            <div 
              className="bg-white rounded-xl shadow-lg p-6 overflow-hidden"
              style={{ border: '2px solid #b27f47' }}
            >
              <h3 className="font-serif-display font-semibold mb-4" style={{ color: '#2d1810' }}>Finn Oss</h3>
              <div className="rounded-xl overflow-hidden h-64" style={{ border: '2px solid #8b4513' }}>
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d1200!2d16.5415!3d68.7985!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x45db6ca050407089%3A0xd2e86c6c5f3c9ea!2sMasala%20Indisk%20restaurant!5e0!3m2!1sno!2sno!4v1710000000000"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="Masala Indisk Restaurant - Harstad"
                ></iframe>
              </div>
            </div>
          </motion.div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ContactPage;
